import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Roles administrativos válidos com suas permissões
export const ADMIN_ROLES = {
  adm_master: {
    label: "Administrador Master",
    level: 100,
    permissions: ["all"]
  },
  gestor: {
    label: "Gestor",
    level: 80,
    permissions: ["dashboard", "negocios", "usuarios", "moderacao", "relatorios", "programas", "categorias", "configuracoes", "banners", "mapa"]
  },
  tecnico: {
    label: "Técnico",
    level: 60,
    permissions: ["dashboard", "negocios", "moderacao", "categorias", "programas", "mapa"]
  },
  editor: {
    label: "Editor",
    level: 50,
    permissions: ["dashboard", "negocios", "negocios_view", "usuarios", "moderacao", "noticias", "banners", "parceiros", "categorias", "tags", "programas"]
  },
  analista: {
    label: "Analista",
    level: 40,
    permissions: ["dashboard", "negocios", "relatorios", "programas"]
  },
  auditor: {
    label: "Auditor",
    level: 20,
    permissions: ["dashboard", "relatorios", "negocios_view", "governanca"]
  }
};

// Cargos que exigem aceite via convite por email
export const INVITE_REQUIRED_ROLES = new Set(["editor", "tecnico", "analista", "auditor"]);

// Helper para verificar se o role tem acesso a uma página/recurso
export const hasPermission = (role, permission) => {
  const roleConfig = ADMIN_ROLES[role];
  if (!roleConfig) return false;
  if (roleConfig.permissions.includes("all")) return true;
  return roleConfig.permissions.includes(permission);
};

// Lista de roles que têm acesso ao admin
const VALID_ADMIN_ROLES = Object.keys(ADMIN_ROLES);

export default function AdminProtectedRoute({ children, requiredPermission = null, adminMasterOnly = false }) {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);

  useEffect(() => {
    const checkAccess = () => {
      let userRole = null;
      
      // Primeiro verificar o contexto de autenticação
      if (isAuthenticated && user) {
        userRole = user.role || 'user';
      } else {
        // Fallback: verificar localStorage (para casos de refresh)
        const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
        const adminUser = localStorage.getItem('adminUser') || localStorage.getItem('user');
        
        if (adminToken && adminUser) {
          try {
            const parsed = JSON.parse(adminUser);
            userRole = parsed.role || 'user';
          } catch (e) {
            userRole = null;
          }
        }
      }

      // Verificar se é um role admin válido
      if (userRole && VALID_ADMIN_ROLES.includes(userRole)) {
        setCurrentRole(userRole);

        // Gate: página exclusiva de adm_master (SEO/IA/integrações/etc)
        if (adminMasterOnly && userRole !== 'adm_master') {
          setHasAccess(false);
        } else if (!requiredPermission) {
          // Se não há permissão específica requerida, permitir acesso
          setHasAccess(true);
        } else {
          // Verificar se tem a permissão específica
          setHasAccess(hasPermission(userRole, requiredPermission));
        }
      } else {
        setHasAccess(false);
        setCurrentRole(null);
      }
      
      setIsChecking(false);
    };

    checkAccess();
  }, [isAuthenticated, user, requiredPermission, adminMasterOnly]);

  // Mostrar loading enquanto verifica
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Redirecionar se não tiver acesso
  if (!hasAccess) {
    // Se tem role admin mas não tem permissão específica, mostrar mensagem de acesso negado
    if (currentRole && (requiredPermission || adminMasterOnly)) {
      const denyReason = adminMasterOnly
        ? 'Esta página é exclusiva do Administrador Master. Solicite acesso ao super-administrador da plataforma.'
        : null;
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Acesso Restrito</h2>
            <p className="text-gray-600 mb-4">
              {denyReason || <>Seu perfil de <strong>{ADMIN_ROLES[currentRole]?.label}</strong> não tem permissão para acessar esta página.</>}
            </p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Voltar
            </button>
          </div>
        </div>
      );
    }
    
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  return children;
}
